import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { MOBILE_SCREEN } from "../../components/theme";
import { QuestionWithRelations } from "../../../../api/src/types";
import VideoContainer from "../../components/VideoContainer";
import ExampleScenario from "./ExampleScenario";
import Protip from "./Protip";
import QuestionRenderer from "./QuestionRenderer";
import { StyledDescription } from "./Questionnaire";
import { Card } from "../../components/layout/Card";
import RequiredAsterisk from "../../components/RequiredAsterisk";

export const CardContainer = styled.div`
  margin: 24px 0;
  ${MOBILE_SCREEN} {
    margin: 16px 0;
  }
`;

const ReadOnlyCardContainer = styled.div`
  margin-bottom: 16px;
  ${MOBILE_SCREEN} {
    margin-bottom: 8px;
  }
`;

const ReadOnlyQuestionTitle = styled.span`
  display: flex;
  text-align: left;
  align-items: center;
  gap: 8px;
`;

interface QuestionProps {
  question: QuestionWithRelations;
  documentId: string;
  shouldValidateQuestionnaireSection?: boolean;
  isReadOnly?: boolean;
}

const Question: React.FC<QuestionProps> = ({
  question,
  documentId,
  shouldValidateQuestionnaireSection,
  isReadOnly = false,
}) => {
  const hasNoAnswers = question.answers.length === 0 && question.isRequired;
  const validationError =
    hasNoAnswers && Boolean(shouldValidateQuestionnaireSection);

  // Memoize selected options with protips based on RTK cache (optimistic patches drive this)
  const selectedOptionsWithProtips = useMemo(() => {
    const selectedIds = new Set(
      question.answers[0]?.answerQuestionOptions?.map(
        (opt) => opt.questionOptionId,
      ) ?? [],
    );
    return question.questionOptions.filter(
      (opt) => selectedIds.has(opt.id) && opt.proTip,
    );
  }, [question.answers, question.questionOptions]);

  if (isReadOnly) {
    return (
      <ReadOnlyCardContainer>
        <Card
          header={{
            title: (
              <ReadOnlyQuestionTitle>
                {question.title}
                {question.isRequired && <RequiredAsterisk />}
              </ReadOnlyQuestionTitle>
            ),
          }}
        >
          <QuestionRenderer
            question={question}
            documentId={documentId}
            isReadOnly={isReadOnly}
          />
        </Card>
      </ReadOnlyCardContainer>
    );
  }

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {question.title}
              {question.isRequired && <RequiredAsterisk />}
            </>
          ),
        }}
        isError={validationError}
      >
        {question.description && (
          <StyledDescription
            dangerouslySetInnerHTML={{
              __html: question.description,
            }}
          />
        )}
        {Array.isArray(question.videos) && question.videos.length > 0 && (
          <VideoContainer
            videoDetails={{
              youtubeLink: question.videos[0]?.youtubeLink ?? "",
              title: question.videos[0]?.title ?? "",
              author: question.videos[0]?.author ?? "",
            }}
          />
        )}
        {question.exampleScenario && (
          <ExampleScenario text={question.exampleScenario} />
        )}
        {question.proTip && <Protip text={question.proTip} />}

        {/*Render the question based on the question type*/}
        <QuestionRenderer question={question} documentId={documentId} />

        {/* Display protips only for selected question options */}
        {selectedOptionsWithProtips.map(
          (option) =>
            option.proTip && <Protip key={option.id} text={option.proTip} />,
        )}
      </Card>
    </CardContainer>
  );
};

export default Question;
